import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { requestForgotPasswordOtp, verifyForgotPasswordOtp } from '../../services/api';
import { ACTION } from '@kioskgaming/ui';

type LocationState = {
  requestToken?: string;
  contact?: string;
  nextResendAt?: string;
};

function secondsUntil(iso: string | null | undefined) {
  if (!iso) return 0;
  const ts = new Date(iso).getTime();
  if (!Number.isFinite(ts)) return 0;
  return Math.max(0, Math.ceil((ts - Date.now()) / 1000));
}

export default function ForgotPasswordOtpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialState = (location.state as LocationState | null) ?? {};
  const [requestToken, setRequestToken] = useState(initialState.requestToken ?? '');
  const [contact] = useState(initialState.contact ?? '');
  const [nextResendAt, setNextResendAt] = useState<string | null>(initialState.nextResendAt ?? null);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    const interval = window.setInterval(() => setTick((x) => x + 1), 250);
    return () => window.clearInterval(interval);
  }, []);

  const resendSeconds = secondsUntil(nextResendAt);
  const canResend = resendSeconds <= 0 && !resending && !loading;

  if (!requestToken || !contact) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 px-4 py-8">
        <div className="mx-auto w-full max-w-md rounded-2xl border border-emerald-100 bg-white p-6 text-center shadow-xl">
          <p className="text-sm text-gray-600">Please start from step 1 to request an OTP.</p>
          <Link to="/forgot-password" className="mt-4 inline-block text-sm font-medium text-emerald-700 hover:underline">
            Go to step 1
          </Link>
        </div>
      </div>
    );
  }

  const onContinue = async () => {
    if (!otp.trim() || otp.trim().length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      const res = await verifyForgotPasswordOtp(requestToken, otp.trim());
      if (!res.success || !res.data?.token) {
        toast.error(res.message || 'OTP verification failed');
        return;
      }
      toast.success('OTP verified');
      navigate('/forgot-password/reset', {
        state: {
          verifiedToken: res.data.token,
          otp: otp.trim(),
          requestToken,
        },
      });
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'OTP verification failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const onResend = async () => {
    if (!canResend) return;
    setResending(true);
    try {
      const res = await requestForgotPasswordOtp(contact);
      if (!res.success || !res.data?.token) {
        toast.error(res.message || 'Failed to resend OTP');
        return;
      }
      setRequestToken(res.data.token);
      setNextResendAt(res.data.next_resend_at ?? null);
      setOtp('');
      toast.success('A new OTP has been sent');
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to resend OTP';
      toast.error(msg);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 px-4 py-8">
      <div className="mx-auto w-full max-w-md rounded-2xl border border-emerald-100 bg-white p-6 shadow-xl shadow-emerald-100/50">
        <p className="text-center text-xs font-medium uppercase tracking-wide text-emerald-600">
          Step 2 of 3
        </p>
        <h1 className="mt-1 text-center text-xl font-bold text-gray-900">Enter OTP</h1>
        <p className="mt-2 text-center text-sm text-gray-600">
          We sent a code to your email or phone. Enter it below.
        </p>

        <div className="mt-6 space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">OTP</label>
            <div className="flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="min-w-0 flex-1 rounded-lg border border-gray-200 px-3 py-2.5 text-center text-lg tracking-widest outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                placeholder="••••••"
                maxLength={6}
              />
              <button
                type="button"
                onClick={onResend}
                disabled={!canResend || loading}
                className="shrink-0 rounded-lg border border-emerald-200 px-3 py-2.5 text-sm font-medium text-emerald-800 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50 tabular-nums min-w-[5.5rem]"
              >
                {resending ? 'Sending…' : canResend ? 'Resend' : `Resend (${resendSeconds}s)`}
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={onContinue}
            disabled={loading}
            className="w-full rounded-lg bg-emerald-600 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            {loading ? 'Verifying…' : 'Continue'}
          </button>
        </div>

        <FooterLinks />
      </div>
    </div>
  );
}

function FooterLinks() {
  return (
    <div className="mt-6 flex flex-col gap-2 text-center text-sm">
      <Link to="/forgot-password" className="text-emerald-700 hover:underline">
        Back to step 1
      </Link>
      <Link to="/login" className="text-gray-500 hover:underline">
        {ACTION.backToSignIn}
      </Link>
    </div>
  );
}
